import { defineComponent as _defineComponent } from 'vue'
import { ref, watch, provide, onMounted, inject } from "vue";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import LineInfoDetailHeader from "@/components/line/LineInfoDetailHeader.vue";
import LineInfoDetailContent from "@/components/line/LineInfoDetailContent.vue";
import LineInfoDetailSelectBusStop from "@/components/line/LineInfoDetailSelectBusStop.vue";
import LineInfoDetailFooter from "@/components/line/LineInfoDetailFooter.vue";
import MapOfBusStop from "@/views/map/MapOfBusStop.vue";
import InfoModal from "@/components/shared/modal/base/InfoModal.vue";
import Page from "@/components/shared/Page.vue";
import SelectModal from "@/components/shared/modal/base/SelectModal.vue";
import router from "@/router";
import { IBusStop, IBusStopData } from "@/domain/entities/busStop";
import { useLineStore } from "@/stores/lineStore";
import { useRoute } from "vue-router";
import { IFavoriteLine } from "@/domain/entities/favoriteLine";
import { IBusLocation, ILineInfo } from "@/domain/entities/lineInfo";
import { computed } from "@vue/reactivity";
import {
  LineTypeCode,
  PROVIDE_KEY_BUS_LOCATIONS,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  ShowErrorModal,
} from "@/types";
import useConvertTimeStrToMinutes from "@/use/useConvertTimeStrToMinutes";
import useConvertCurrentTimeToMinutes from "@/use/useConvertCurrentTimeToMinutes";
import TimeTableModal from "@/components/line/TimeTableModal.vue";
import PageModal from "@/components/shared/modal/base/PageModal.vue";


const __default__ = {
  name: "LineInfoDetail",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose: __expose }) {
  __expose();

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const lineStore = useLineStore();
const route = useRoute();

/** UI 바인딩을 위한 노선 정보 */
const lineId = route.params.lineId.toString();
const lineInfo = ref<ILineInfo>();
const footer = ref<typeof LineInfoDetailFooter>();

const availableTimes = computed(() => {
  if (lineInfo.value?.lineTypeCode === LineTypeCode.Backward) {
    return lineInfo.value?.schedules?.map(
      (schedule, index) =>
        `${schedule}(${lineInfo.value?.routeName}${
          lineInfo.value?.turns[index].turnNum ?? ""
        })`
    );
  }
  return lineInfo.value?.schedules ?? [];
});
const selectedTimeIndex = ref(0);
const selectedTurn = computed(
  () => lineInfo?.value?.turns[selectedTimeIndex.value]
);
const isInsideShuttle = computed(
  () => lineInfo.value?.lineTypeCode === LineTypeCode.Inside
);

const currentFavoriteLine = ref<IFavoriteLine>();

const selectedGetOnBusStop = ref<IBusStop | undefined>(undefined);
const selectedGetOffBusStop = ref<IBusStop | undefined>(undefined);

selectedGetOnBusStop.value = lineStore.getBusStopById(
  route.params.getOnBusStopId?.toString() ?? ""
);
selectedGetOffBusStop.value = lineStore.getBusStopById(
  route.params.getOffBusStopId?.toString() ?? ""
);

const isFavoriteLine = computed(() => !!currentFavoriteLine.value);
const showFooter = ref(false);

const showSelectTimeModal = ref(false);
const toggleSelectTimeModal = (show: boolean) => {
  showSelectTimeModal.value = show;
};
const onTimeSelected = (index: number) => {
  selectedTimeIndex.value = index;
  getBusLocation();
  setTimeout(() => {
    showSelectTimeModal.value = false;
  }, 300);
};

const checkFavoriteLine = () => {
  const favoriteLine = lineStore.getFavoriteLine(
    selectedTurn.value?.id ?? "",
    selectedGetOnBusStop.value?.id ?? "",
    selectedGetOffBusStop.value?.id ?? ""
  );
  currentFavoriteLine.value = favoriteLine;
};

watch([selectedTimeIndex, showFooter], ([selectedTimeIndex, showFooter]) => {
  if (!showFooter) return;
  checkFavoriteLine();
});

/** 버스 위치 가져오기 */
const busLocations = ref<IBusLocation[]>([]);
provide(PROVIDE_KEY_BUS_LOCATIONS, busLocations);

// 종점 정류장 도착시간과 현재 시간과의 차이 (분)
const getTimeDiffMinFromLastArrivalTime = () => {
  if (!selectedTurn.value) return -1;
  if (!lineInfo.value) return -1;

  // 종점 정류장
  const lastBusStop = isInsideShuttle.value
    ? lineInfo.value.turns[lineInfo.value.turns.length - 1].lastStop
    : selectedTurn.value.lastStop;

  const timeDiffMinFromLastArrivalTime =
    useConvertTimeStrToMinutes(lastBusStop.arrivalTime) -
    useConvertCurrentTimeToMinutes();
  return timeDiffMinFromLastArrivalTime;
};

// 기점 출발시간과 현재 시간과의 차이 (분)
const getTimeDiffMinFromLineStartTime = () => {
  if (!selectedTurn.value) return -1;
  if (!lineInfo.value) return -1;

  // 기점 정류장
  const firstStop = isInsideShuttle.value
    ? lineInfo.value.turns[0].firstStop
    : selectedTurn.value.firstStop;

  return (
    useConvertTimeStrToMinutes(firstStop.arrivalTime) -
    useConvertCurrentTimeToMinutes()
  );
};

// 현재시간이 기점 출발 시간 20분전 ~ 종점 정류장 도착시간 사이에 있는 경우에만 버스위치 API 호출
const shouldGetBusLocation = () => {
  return (
    getTimeDiffMinFromLastArrivalTime() > 0 &&
    getTimeDiffMinFromLineStartTime() <= 20
  );
};

const getBusLocation = async () => {
  if (!shouldGetBusLocation()) {
    busLocations.value = [];
    return;
  }

  const busLocationResult = await lineStore.getBusLocations(
    lineInfo.value?.id ?? "",
    isInsideShuttle.value ? undefined : selectedTurn.value?.id ?? ""
  );
  busLocations.value = busLocationResult.data ?? [];
};

const shouldShowMap = ref(false);
const selectedBusStopForLocation = ref<IBusStopData>();
const showMap = (busStop: IBusStopData) => {
  selectedBusStopForLocation.value = busStop;
  shouldShowMap.value = true;
};
const toggleShowMap = (show: boolean) => {
  shouldShowMap.value = show;
};

const showBusStopTimeGuide = ref(false);

const getLineInfoDetail = async () => {
  const lineInfoResult = await lineStore.getLineInfoDetail(lineId);
  if (!lineInfoResult.isSuccess) {
    showErrorModal(
      "노선 상세 오류",
      lineInfoResult.errorMessage,
      lineInfoResult.resultCode,
      lineInfoResult.errorTime,
      router.back
    );
    return;
  }
  lineInfo.value = lineInfoResult.data;
};

// 순환노선(셔틀)인 경우
const setCircularLineInfoIfNeeded = () => {
  if (!isInsideShuttle.value) return;
  const busStops = lineInfo.value?.turns[0]?.busStops;
  if (!busStops) return;
  selectedGetOnBusStop.value = busStops.find((busStop) => busStop.isGetOnStop);
  selectedGetOffBusStop.value = busStops.find(
    (busStop) => busStop.isGetOffStop
  );
  checkFavoriteLine();
  if (!isFavoriteLine.value) {
    footer.value?.showTips(5000);
  }
};
/**  순환노선(셔틀) 노선 시간표 보기 */
const shouldShowTimeTable = ref(false);
const toggleShowTimeTable = (show: boolean) => {
  shouldShowTimeTable.value = show;
};

const targetBusStopForTimeTable = ref<IBusStop>();
const showTimeTable = (busStop: IBusStop) => {
  targetBusStopForTimeTable.value = busStop;
  toggleShowTimeTable(true);
};

onMounted(async () => {
  await getLineInfoDetail();
  setCircularLineInfoIfNeeded();
  getBusLocation();
});

const __returned__ = { showErrorModal, lineStore, route, lineId, lineInfo, footer, availableTimes, selectedTimeIndex, selectedTurn, isInsideShuttle, currentFavoriteLine, selectedGetOnBusStop, selectedGetOffBusStop, isFavoriteLine, showFooter, showSelectTimeModal, toggleSelectTimeModal, onTimeSelected, checkFavoriteLine, busLocations, getTimeDiffMinFromLastArrivalTime, getTimeDiffMinFromLineStartTime, shouldGetBusLocation, getBusLocation, shouldShowMap, selectedBusStopForLocation, showMap, toggleShowMap, showBusStopTimeGuide, getLineInfoDetail, setCircularLineInfoIfNeeded, shouldShowTimeTable, toggleShowTimeTable, targetBusStopForTimeTable, showTimeTable, NavigationBar, LineInfoDetailHeader, LineInfoDetailContent, LineInfoDetailSelectBusStop, MapOfBusStop, InfoModal, Page, SelectModal, TimeTableModal, PageModal }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})